/**
 * Retire Asset Dialog
 * Confirmation dialog for retiring an asset from active tracking.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

import * as api from '../../services/api';
import { colorPalette } from '../../theme';

const RetireAssetDialog = ({ open, onClose, asset, onRetired }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleRetire = async () => {
    if (!asset) return;
    setLoading(true);
    setError(null);

    try {
      await api.updateAsset(asset.id, {
        status: 'retired',
        retiredAt: new Date().toISOString(),
      });
      if (onRetired) onRetired(asset);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to retire asset');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" PaperProps={{ sx: { maxWidth: 400 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.5rem', fontWeight: 600 }}>
        <WarningIcon sx={{ color: colorPalette.canary, fontSize: '2rem' }} />
        Retire Asset
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body1" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          Are you sure you want to retire <strong>{asset?.name || asset?.unitNumber || 'this asset'}</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Retired assets will be removed from active tracking and map views.
          This action can be reversed by changing the asset status back to active.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleRetire}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{ 
            backgroundColor: colorPalette.asseTracOrange,
            '&:hover': { backgroundColor: (theme) => theme.palette.primary.dark }
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RetireAssetDialog;
